// Kunden-Version: nach dem Absenden. Erst bezahlen (1,99 €, Verbraucher-AGB
// und Verzicht auf das Widerrufsrecht), dann wartet es auf die Auswertung und zeigt das
// Ergebnis (Volumen als Spanne, ohne Gewicht und Fahrten), das PDF und "An Umzugsfirmen
// senden": am Handy das Teilen-Menü mit PDF und Anfragetext, am PC Download
// plus vorbereitete E-Mail. Versand nie über unsere Server.

import { useEffect, useState } from "react";
import { AGB_VERBRAUCHER_URL, WAIVER_TEXT } from "@umzugsscan/shared";
import { API_URL, ApiError, api } from "../api.ts";
import { useDraft, useStore } from "../hooks.ts";
import { forgetLink } from "../speicher.ts";
import { errorText } from "../texte.ts";
import { Button, Check, Dialog } from "../ui/elemente.tsx";
import { requestText } from "../anfrage.ts";
import { KaufAbgebrochen, inPlayApp, kaufen, offeneKaeufe, playDienst, playPreis, type DigitalGoods } from "../play.ts";

/** Handy (Touch + Teilen mit Dateien) oder PC? */
function canShareFiles(): boolean {
  try {
    const coarse = window.matchMedia("(pointer: coarse)").matches;
    const probe = new File([new Blob(["x"])], "x.pdf", { type: "application/pdf" });
    return coarse && typeof navigator.canShare === "function" && navigator.canShare({ files: [probe] });
  } catch {
    return false;
  }
}

/**
 * Rückkehr von der Zahlungsseite (12.10.2026): ?zahlung=ok&sid=… steht nur einmal in der Adresse.
 * Der Stand wird je Aufnahme im Tab gemerkt (überlebt Neuladen) und die Adresse bereinigt; "abbruch"
 * gilt nur für diesen einen Aufruf.
 */
type Rueckkehr = { status: "ok" | "abbruch"; sid: string | null; seit: number };
const rueckkehrKey = (token: string) => `umzugsscan:zahlung:${token}`;
function readRueckkehr(token: string): Rueckkehr | null {
  const q = new URLSearchParams(window.location.search);
  const status = q.get("zahlung");
  if (status === "ok" || status === "abbruch") {
    const r: Rueckkehr = { status, sid: q.get("sid"), seit: Date.now() };
    try {
      if (status === "ok") sessionStorage.setItem(rueckkehrKey(token), JSON.stringify(r));
      else sessionStorage.removeItem(rueckkehrKey(token));
    } catch {
      // Speicher gesperrt: dann gilt der Stand nur bis zum Neuladen.
    }
    window.history.replaceState(null, "", window.location.pathname);
    return r;
  }
  try {
    return JSON.parse(sessionStorage.getItem(rueckkehrKey(token)) ?? "null") as Rueckkehr | null;
  } catch {
    return null;
  }
}
function clearRueckkehr(token: string) {
  try {
    sessionStorage.removeItem(rueckkehrKey(token));
  } catch {
    // egal
  }
}
/** So lange gilt "Zahlung wird bestätigt", danach Hinweis und Weg zum erneuten Bezahlen. */
const BESTAETIGEN_MS = 90_000;

export function PrivatErgebnis() {
  const d = useDraft();
  const store = useStore();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [askDelete, setAskDelete] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const pay = d.privat?.payment;
  const unpaid = pay?.required === true;
  const waiting = !unpaid && (d.status === "eingereicht" || d.status === "in_auswertung");
  // Zurück von der Zahlungsseite: aktiv bei Stripe nachfragen, bis die Zahlung bestätigt ist.
  const [rueckkehr, setRueckkehr] = useState(() => readRueckkehr(d.token));
  const returned = rueckkehr?.status ?? null;
  const confirming = unpaid && returned === "ok";
  const [now, setNow] = useState(() => Date.now());
  const lange = confirming && now - (rueckkehr?.seit ?? now) > BESTAETIGEN_MS;
  const pdfUrl = `${API_URL}/erfassung/${encodeURIComponent(d.token)}/kundenbericht.pdf`;

  // Bezahlt: gemerkte Rückkehr vergessen.
  useEffect(() => {
    if (!unpaid && rueckkehr) clearRueckkehr(d.token);
  }, [unpaid, rueckkehr, d.token]);

  // Zahlung bestätigen: sofort und dann alle 3 Sekunden beim Server nachfragen (er fragt Stripe).
  useEffect(() => {
    if (!confirming) return;
    let alive = true;
    const check = async () => {
      try {
        const r = await api.request<{ paid: boolean }>("POST", `${api.survey(d.token)}/zahlung-pruefen`, rueckkehr?.sid ? { sid: rueckkehr.sid } : {});
        if (alive && r.paid) await store.refresh();
      } catch {
        // Netz weg: beim nächsten Durchlauf erneut.
      }
      if (alive) setNow(Date.now());
    };
    void check();
    const t = window.setInterval(() => void check(), 3000);
    return () => {
      alive = false;
      window.clearInterval(t);
    };
  }, [confirming, rueckkehr, d.token, store]);

  // Während der Auswertung alle 4 Sekunden nachsehen.
  useEffect(() => {
    if (!waiting) return;
    const t = window.setInterval(() => void store.refresh(), 4000);
    return () => window.clearInterval(t);
  }, [waiting, store]);

  if (deleted)
    return (
      <section className="danke">
        <h1>Aufnahme gelöscht</h1>
        <p>Ihre Angaben, Fotos und Ihre E-Mail-Adresse sind bei uns gelöscht.</p>
        <p>
          <a href="/">Neue Aufnahme beginnen</a>
        </p>
      </section>
    );

  const retry = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.request("POST", `${api.survey(d.token)}/neu-auswerten`);
      await store.refresh();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const fetchPdf = async (): Promise<Blob> => {
    const res = await fetch(pdfUrl);
    if (!res.ok) throw new Error(`PDF ${res.status}`);
    return res.blob();
  };

  const send = async () => {
    setBusy(true);
    setError(null);
    setInfo(null);
    const text = requestText(d);
    const fileName = d.privat?.result?.fileName ?? "Umzugsaufnahme.pdf";
    try {
      const blob = await fetchPdf();
      if (canShareFiles()) {
        const file = new File([blob], fileName, { type: "application/pdf" });
        try {
          await navigator.share({ files: [file], title: text.subject, text: text.body });
        } catch (e) {
          if ((e as Error).name !== "AbortError") throw e;
        }
      } else {
        // PC: PDF herunterladen, dann E-Mail-Programm mit fertigem Text öffnen.
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 10_000);
        const body = `${text.body}\n\n(Bitte die heruntergeladene Datei ${fileName} anhängen.)`;
        window.location.href = `mailto:?subject=${encodeURIComponent(text.subject)}&body=${encodeURIComponent(body)}`;
        setInfo(`Das PDF wurde heruntergeladen. Hängen Sie es an die E-Mail an, die sich gerade geöffnet hat.`);
      }
    } catch (e) {
      console.error(e);
      setError("Das PDF konnte gerade nicht geladen werden. Bitte gleich noch einmal versuchen.");
    } finally {
      setBusy(false);
    }
  };

  const copyText = async () => {
    try {
      await navigator.clipboard.writeText(requestText(d).body);
      setInfo("Anfragetext kopiert.");
    } catch {
      setInfo("Kopieren nicht möglich – bitte den Text markieren und kopieren.");
    }
  };

  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      await store.remove();
      forgetLink("k", "privat");
      setDeleted(true);
    } catch (e) {
      setError(errorText(e));
      setBusy(false);
      setAskDelete(false);
    }
  };

  const r = d.privat?.result;
  if (unpaid)
    return (
      <Bezahlen
        token={d.token}
        priceCents={pay!.priceCents}
        confirming={confirming}
        lange={lange}
        onNeu={() => {
          clearRueckkehr(d.token);
          setRueckkehr(null);
        }}
        cancelled={returned === "abbruch"}
        playSku={pay!.playBilling ? (pay!.playProductId ?? null) : null}
      />
    );
  return (
    <section className="ergebnis">
      {waiting && (
        <div className="danke">
          <div className="spinner" style={{ margin: "0 auto 16px" }} aria-label="Wird ausgewertet" />
          <h1>Ihre Aufnahme wird ausgewertet</h1>
          <p>Das dauert meist 1 bis 3 Minuten. Sie können die Seite offen lassen; wir schicken Ihnen außerdem eine E-Mail, sobald das PDF fertig ist.</p>
        </div>
      )}

      {d.status === "fehler" && (
        <div className="card card-warn">
          <h2>Die Auswertung hat nicht geklappt</h2>
          <p>Ihre Fotos und Angaben sind gespeichert. Bitte versuchen Sie es noch einmal.</p>
          <Button onClick={() => void retry()} disabled={busy}>
            Erneut auswerten
          </Button>
        </div>
      )}

      {r && (
        <>
          <div className="danke">
            <div className="check-big" aria-hidden="true">
              ✓
            </div>
            <h1>Ihre Umzugsaufnahme ist fertig</h1>
          </div>
          <div className="kacheln zwei">
            {r.volumeText && (
              <div className="kachel kachel-volumen">
                <b>{r.volumeText} m³</b>
                <span>Umzugsvolumen (geschätzt)</span>
              </div>
            )}
            <div className="kachel">
              <b>{r.rooms.length}</b>
              <span>Räume</span>
            </div>
            <div className="kachel">
              <b>{r.itemCount}</b>
              <span>Möbel und Gegenstände</span>
            </div>
            <div className="kachel">
              <b>ca. {r.boxesRough}</b>
              <span>Umzugskartons</span>
            </div>
          </div>

          <div className="stack">
            <Button onClick={() => void send()} disabled={busy}>
              {busy ? "Einen Moment …" : "An Umzugsfirmen senden"}
            </Button>
            <a className="btn btn-secondary btn-wide btn-link" href={pdfUrl} target="_blank" rel="noopener">
              PDF ansehen
            </a>
          </div>
          {info && <p className="tip">{info}</p>}

          <section className="card">
            <h2>Anfragetext</h2>
            <p className="hint">Wird beim Senden mitgeschickt. Sie können ihn auch kopieren und selbst verwenden.</p>
            <pre className="anfrage">{requestText(d).body}</pre>
            <Button variant="secondary" onClick={() => void copyText()}>
              Text kopieren
            </Button>
          </section>

          <section className="card">
            <h2>Ihr Aufnahme-Code: {d.privat?.code}</h2>
            <p className="hint">
              Steht auch im PDF. Umzugsfirmen mit Umzugsscan-Konto können Ihre Aufnahme mit diesem Code übernehmen, aber nur, wenn
              Sie per E-Mail zustimmen.
            </p>
            {(d.privat?.transfers ?? []).length > 0 && (
              <ul className="freigaben">
                {d.privat!.transfers!.map((t, i) => (
                  <li key={i}>
                    {t.companyName}:{" "}
                    {t.status === "bestaetigt" ? "übernommen" : t.status === "abgelehnt" ? "abgelehnt" : "wartet auf Ihre Zustimmung (Link in der E-Mail)"}
                  </li>
                ))}
              </ul>
            )}
          </section>

          <details className="card">
            <summary>Räume und Möbel ansehen</summary>
            {r.rooms.map((room, i) => (
              <div key={i} className="raum-liste">
                <h3>{room.name}</h3>
                {room.items.length ? (
                  <ul>
                    {room.items.map((it, j) => (
                      <li key={j}>
                        {it.qty > 1 ? `${it.qty} × ` : ""}
                        {it.name}
                        {it.disposal ? " (Entsorgung)" : ""}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="hint">Keine Möbel erkannt.</p>
                )}
              </div>
            ))}
            <p className="hint">Mengen sind eine KI-gestützte Schätzung aus Ihren Fotos.</p>
          </details>
        </>
      )}

      {error && <p className="error">{error}</p>}

      {!waiting && (
        <div className="stack">
          <Button variant="quiet" onClick={() => setAskDelete(true)}>
            Aufnahme löschen
          </Button>
        </div>
      )}
      {askDelete && (
        <Dialog
          title="Aufnahme wirklich löschen?"
          onClose={() => setAskDelete(false)}
          actions={
            <>
              <Button variant="danger" onClick={() => void remove()} disabled={busy}>
                Endgültig löschen
              </Button>
              <Button variant="quiet" onClick={() => setAskDelete(false)}>
                Abbrechen
              </Button>
            </>
          }
        >
          <p>Ihre Angaben, alle Fotos, das Ergebnis und Ihre E-Mail-Adresse werden sofort gelöscht. Das lässt sich nicht rückgängig machen.</p>
        </Dialog>
      )}
    </section>
  );
}

const euro = (cents: number) => (cents / 100).toLocaleString("de-DE", { style: "currency", currency: "EUR" });

/** Bezahlen vor der Auswertung. Freigeschaltet wird erst nach Bestätigung durch den Zahlungsanbieter. */
function Bezahlen(props: {
  token: string;
  priceCents: number;
  confirming: boolean;
  /** Bestätigung dauert schon lange (Lastschrift oder abgebrochen). */
  lange: boolean;
  onNeu: () => void;
  cancelled: boolean;
  playSku: string | null;
}) {
  const store = useStore();
  // Play-Store-App: Bezahlung über Google Play Billing statt Stripe (Zahlungsrichtlinie von Google).
  // "pruefen" = noch nicht klar; "play" = Billing da; "nein" = normaler Browser (Stripe); "app-ohne" = in der App, aber ohne Billing.
  const [modus, setModus] = useState<"pruefen" | "play" | "nein" | "app-ohne">("pruefen");
  const [dienst, setDienst] = useState<DigitalGoods | null>(null);
  const [playPreisText, setPlayPreisText] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    void (async () => {
      const imApp = inPlayApp();
      const s = imApp && props.playSku ? await playDienst() : null;
      if (!alive) return;
      if (s && props.playSku) {
        setDienst(s);
        setPlayPreisText(await playPreis(s, props.playSku));
        if (alive) setModus("play");
      } else setModus(imApp ? "app-ohne" : "nein");
    })();
    return () => {
      alive = false;
    };
  }, [props.playSku]);
  const [agb, setAgb] = useState(false);
  const [verzicht, setVerzicht] = useState(false);
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(props.cancelled ? "Die Zahlung wurde abgebrochen. Sie können es jederzeit erneut versuchen." : null);

  if (props.confirming)
    return (
      <section className="danke">
        <div className="spinner" style={{ margin: "0 auto 16px" }} aria-label="Zahlung wird bestätigt" />
        <h1>Danke für Ihre Zahlung</h1>
        <p>Sobald der Zahlungsanbieter die Zahlung bestätigt, startet die Auswertung. Bei Karte und PayPal geht das meist sofort, bei Lastschrift kann es etwas dauern. Wir schicken Ihnen eine E-Mail, sobald das PDF fertig ist.</p>
        {props.lange && (
          <>
            <p className="hint">
              Die Bestätigung dauert länger als üblich. Bei Lastschrift ist das normal: Sie können die Seite schließen, die E-Mail kommt trotzdem. Wurde die Zahlung
              nicht abgeschlossen, können Sie es erneut versuchen.
            </p>
            <Button variant="secondary" onClick={props.onNeu}>
              Zahlung nicht abgeschlossen? Erneut bezahlen
            </Button>
          </>
        )}
      </section>
    );

  // In der Play-Store-App ohne Billing gibt es keinen Stripe-Weg (Zahlungsrichtlinie von Google): nur der Hinweis.
  if (modus === "app-ohne")
    return (
      <section className="ergebnis">
        <div className="danke">
          <div className="check-big" aria-hidden="true">
            ✓
          </div>
          <h1>Ihre Aufnahme ist vollständig</h1>
          <p>Ihre Fotos und Angaben sind gespeichert.</p>
        </div>
        <section className="card">
          <h2>Bezahlung gerade nicht möglich</h2>
          <p>In der App ist die Bezahlung der Auswertung im Moment nicht verfügbar. Ihre Aufnahme bleibt gespeichert; bitte versuchen Sie es später noch einmal.</p>
          <Button onClick={() => void store.refresh()}>Erneut versuchen</Button>
        </section>
      </section>
    );

  /** Google Play: offene Käufe einlösen, sonst kaufen; Server prüft und schaltet frei, dann verbrauchen. */
  const payPlay = async () => {
    const sku = props.playSku;
    if (!dienst || !sku) return;
    const send = (purchaseToken: string) =>
      api.request<{ paid: boolean }>("POST", `${api.survey(props.token)}/play-bezahlen`, { agb: true, verzicht: true, purchaseToken, productId: sku });
    // Ein früher bezahlter, aber nicht verbrauchter Kauf (z. B. Verbindung weg nach dem Kauf) wird wiederverwendet.
    for (const t of await offeneKaeufe(dienst, sku)) {
      try {
        await send(t);
        await dienst.consume(t).catch(() => {});
        await store.refresh();
        return;
      } catch (e) {
        // Schon für eine andere Aufnahme benutzt: verbrauchen, damit er nicht im Weg steht.
        if (e instanceof ApiError && e.code === "play_kauf_ungueltig") await dienst.consume(t).catch(() => {});
        else throw e;
      }
    }
    const kauf = await kaufen(sku);
    try {
      await send(kauf.token);
    } catch (e) {
      await kauf.abschliessen(false).catch(() => {});
      throw e;
    }
    await kauf.abschliessen(true).catch(() => {});
    await dienst.consume(kauf.token).catch(() => {});
    await store.refresh();
  };

  const pay = async () => {
    setTried(true);
    if (!agb || !verzicht) return;
    setBusy(true);
    setError(null);
    if (modus === "play") {
      try {
        await payPlay();
      } catch (e) {
        if (!(e instanceof KaufAbgebrochen)) setError(errorText(e));
        else setError("Der Kauf wurde abgebrochen. Sie können es jederzeit erneut versuchen.");
      } finally {
        setBusy(false);
      }
      return;
    }
    try {
      const r = await api.request<{ url: string | null; paid: boolean }>("POST", `${api.survey(props.token)}/bezahlen`, { agb: true, verzicht: true });
      if (r.url) window.location.href = r.url;
      else await store.refresh();
    } catch (e) {
      setError(errorText(e));
      setBusy(false);
    }
  };

  return (
    <section className="ergebnis">
      <div className="danke">
        <div className="check-big" aria-hidden="true">
          ✓
        </div>
        <h1>Ihre Aufnahme ist vollständig</h1>
        <p>Nach der Zahlung ermittelt unsere künstliche Intelligenz aus Ihren Fotos und Angaben das Umzugsvolumen und den Umfang. Sie bekommen das PDF mit Volumen, Räumen, Möbeln, Kartonzahl und Fotos hier und per E-Mail.</p>
      </div>
      <section className="card">
        {/* In der Play-App der Preis von Google Play (je Land mit dessen Steuer, 03.11.2026), sonst unser Preis. */}
        <h2>Auswertung: {modus === "play" && playPreisText ? playPreisText : euro(props.priceCents)}</h2>
        <p className="hint">
          {modus === "play"
            ? "Einmalig, inklusive Umsatzsteuer. Die Zahlung läuft über Google Play."
            : modus === "nein"
              ? "Einmalig, inklusive 19 % Umsatzsteuer. Zahlung über Stripe, z. B. mit Karte, PayPal oder Lastschrift."
              : "Einmalig, inklusive Umsatzsteuer."}
        </p>
        <Check checked={agb} onChange={setAgb}>
          Ich akzeptiere die{" "}
          <a href={AGB_VERBRAUCHER_URL} target="_blank" rel="noopener">
            AGB
          </a>
          . <span className="req">(erforderlich)</span>
        </Check>
        <Check checked={verzicht} onChange={setVerzicht}>
          {WAIVER_TEXT} <span className="req">(erforderlich)</span>
        </Check>
        {tried && (!agb || !verzicht) && <p className="error">Bitte beide Punkte bestätigen.</p>}
        {error && <p className="error">{error}</p>}
        <Button onClick={() => void pay()} disabled={busy || modus === "pruefen"}>
          {busy
            ? "Einen Moment …"
            : modus === "pruefen"
              ? "Zahlung wird vorbereitet …"
              : modus === "play"
              ? `Mit Google Play bezahlen · ${playPreisText ?? euro(props.priceCents)}`
              : `Jetzt ${euro(props.priceCents)} bezahlen`}
        </Button>
      </section>
    </section>
  );
}
